import { useState, useEffect } from "react";
import { BackArrow, SaveIcon, TaskIcon } from "allIcons";
import {
  BodyContainer,
  TextEditor,
  HeaderContainer,
  IconWrapper,
  InputField,
  ListCard,
  PreviewCard,
  PrimaryHeading,
  RightMainContainer,
  SecondaryHeading,
  ListIconWrapper,
  MainButton,
  AddComponent,
  DeleteButton,
  PopOver,
  AddFromTemplateLibrary,
  DropDownCard,
  Loader,
  ToggleCard,
  SelectField,
  SortableList,
} from "commonUI";
import { useParams } from "react-router-dom";
import { getUserSavePermission, isEqual, parse, useUtilities } from "helpers";
import { useTasks } from "api/templateLibrary/tasks";
import { useTags } from "api/siteSettings/Tags";
import { ModulePrefixes, NotificationType } from "enums";
import {
  useAddChecklistTemplate,
  useCheckListTemplates,
  useUpdateChecklistTemplate,
  useDeleteChecklistTemplate,
} from "api/templateLibrary/checklists";
import { useUserPermissions, useUserRole } from "hooks/user";
import { usePhases } from "api/templateLibrary/phases";
import { t } from "i18next";

export default function AddEditChecklist({previousPath = `/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists`, isSiteTemplate}) {
  const { id } = useParams();
  const { notification, navigate } = useUtilities();
  const { data: tasks } = useTasks();
  const { data: tags = [] } = useTags();
  const { mutateAsync: addChecklist, isLoading: addingChecklist } =
    useAddChecklistTemplate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists/`);
  const { data: checklists, isLoading: loadingChecklists } =
    useCheckListTemplates();
  const { mutateAsync: updateChecklist, isLoading: updatingChecklist } =
    useUpdateChecklistTemplate();
  const { mutateAsync: deleteChecklist, isLoading: deletingChecklist } =
    useDeleteChecklistTemplate();
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();
  const { data: phases, isLoading: loadingPhases } = usePhases();

  const { isSuperAdmin } = useUserRole();

  const [popoverStatus, setPopoverStatus] = useState({
    task: false,
    tag: false,
  });

  const [inputValues, setInputValues] = useState({
    assign_to: [],
    due_date: "",
    website_id: null,
    title: "",
    message: "",
    tags: [],
    priority: [],
    tasks: [],
    phase_id: "",
    phase_due_date_auto: 0,
  });

  useEffect(() => {
    if (isEqual(id, "add") || loadingChecklists) {
      return;
    }
    if (checklists.length !== 0) {
      const checklist = checklists.find((item) => isEqual(item.id, id));
      if (checklist) {
        setInputValues((pre) => ({
          ...pre,
          ...checklist,
          tasks: parse(checklist.tasks) ?? [],
          tags: parse(checklist.tags) ?? [],
        }));
      }
    }
  }, [checklists, loadingChecklists, id]);

  function handleChangeInput(key, value) {
    setInputValues({
      ...inputValues,
      [key]: value,
    });
  }
  function handlePopoverChange(key, value) {
    setPopoverStatus({
      ...popoverStatus,
      [key]: value,
    });
  }

  function hanldeSaveChanges() {
    if (!inputValues.title.trim()) {
      notification({
        type: NotificationType.ERROR,
        message: t("libraryTemplate.checklist.input.checklistTitle.required"),
      });
      return;
    }
    if (isEqual(id, "add")) {
      addChecklist({ ...inputValues, system_status: isSuperAdmin ? "1" : "0" });
      return;
    } else {
      updateChecklist(inputValues);
      return;
    }
  }

  const handleChecklistTaskDragEnd = (event) => {
    const { active: sourceElement, over: targetElement } = event;
    if (!targetElement || !sourceElement) return;
    if (sourceElement?.id !== targetElement?.id) {
      const test = { ...inputValues };
      const newItems = [...test.tasks];
      const [reorderedItem] = newItems.splice(sourceElement.id, 1);
      newItems.splice(targetElement.id, 0, reorderedItem);
      test.tasks = newItems;
      setInputValues(test);
    }
  };

  return (
    <>
      <RightMainContainer>
        <Loader
          loader="block"
          show={addingChecklist || updatingChecklist || deletingChecklist}
        />
        <HeaderContainer>
          <IconWrapper>
            <BackArrow
              onClick={() => navigate(previousPath)}
            />
            <PrimaryHeading>
              {isEqual(id, "add")
                ? t("libraryTemplate.checklist.addChecklist")
                : inputValues.title}
            </PrimaryHeading>
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={
                !isSiteTemplate &&
                !isEqual(id, "add") &&
                !(isEqual(inputValues.system_status, 1) && !isSuperAdmin) &&
                delete_templates
              }
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteButtonText={t("libraryTemplate.delete.button")}
              deleteMessage={t("libraryTemplate.delete.message")}
              onDelete={() => deleteChecklist(id)}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_templates,
              editPermission: edit_templates,
            }) ? (
              <MainButton onClick={() => hanldeSaveChanges()}>
                <SaveIcon />
                Save Changes
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            label={t("libraryTemplate.checklist.input.checklistTitle.label")}
            placeholder={t(
              "libraryTemplate.checklist.input.checklistTitle.placeholder"
            )}
            value={inputValues.title}
            setValue={(value) => handleChangeInput("title", value)}
            disabled={isEqual(inputValues.system_status, 1)}
          />
          <TextEditor
            label={t("common.input.description.label")}
            placeholder={t("common.input.description.placeholder")}
            editorClass="mt-2"
            option={false}
            rows={5}
            value={inputValues.message}
            setValue={(value) => handleChangeInput("message", value)}
          />
          <SelectField
            label={t("common.input.associatedPhase.label")}
            placeholder={t("common.input.associatedPhase.placeholder")}
            className="mt-3 gap-10 mb-3"
            value={inputValues.phase_id}
            setValue={(value) =>
              setInputValues({ ...inputValues, phase_id: value })
            }
          >
            {phases.map((phase) => (
              <option key={phase.id} value={phase.id}>
                {phase.title}
              </option>
            ))}
          </SelectField>
          <ToggleCard
            labelClass="mb-2"
            label={t("common.input.dueDate.automaticallyBasedOnPhase")}
            value={isEqual(inputValues.phase_due_date_auto, 1)}
            setValue={(value) =>
              setInputValues({
                ...inputValues,
                phase_due_date_auto: value ? 1 : 0,
              })
            }
            isLoading={loadingPhases}
            disabled={!inputValues.phase_id}
          />
          <div className="mt-3">
            <SecondaryHeading>
              {t("libraryTemplate.tasks.title")}
            </SecondaryHeading>
            <SortableList
              onDragEnd={(event) => handleChecklistTaskDragEnd(event)}
              items={inputValues.tasks.map((o, index) => {
                if (o.hasOwnProperty("id")) {
                  return index;
                }
                return null;
              })}
            >
              {inputValues.tasks.map((task, index) => {
                return (
                  <ListCard
                    id={index}
                    key={task.id}
                    data={`${index + 1}. ${task.title}`}
                    edit={true}
                    // TODO: update back link
                    link={`/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/${task.id}?backLink=/checklist/add`}
                    strictLink={true}
                    deleteIcon={true}
                    showModalonCross
                    allSorting
                    onCross={() => {
                      handleChangeInput(
                        "tasks",
                        inputValues.tasks.filter((_) => !isEqual(_.id, task.id))
                      );
                    }}
                    icon={
                      <ListIconWrapper>
                        <TaskIcon />
                      </ListIconWrapper>
                    }
                    deleteMessage={t("common.remove.itemFromTemplate.message")}
                    deleteTitle={t("common.remove.itemFromTemplate.title")}
                    crossIcon
                  />
                );
              })}
            </SortableList>
          </div>
          <AddComponent
            title={t("libraryTemplate.checklist.input.addTask")}
            className="mt-3 mb-3"
            value={popoverStatus.task}
            onClick={() => handlePopoverChange("task", true)}
          >
            <PopOver
              open={popoverStatus.task}
              onClose={() => handlePopoverChange("task", false)}
              style={{
                top: "auto",
                left: "auto",
                right: "auto",
                bottom: "calc(100% + 15px)",
              }}
              toolTip={{ vertical: "bottom", horizontal: "center" }}
            >
              <AddFromTemplateLibrary
                heading={t("libraryTemplate.checklist.input.addTask")}
                data={tasks
                  .filter(
                    (task) =>
                      !inputValues.tasks.some((item) => item.id === task.id)
                  )
                  .map((task) => ({
                    title: task.title,
                    icon: <TaskIcon />,
                    onClick: () => {
                      handleChangeInput("tasks", [
                        ...inputValues.tasks,
                        {
                          id: task.id,
                          title: task.title,
                          message: task.message ?? task.description,
                        },
                      ]);
                    },
                  }))}
                emptyTemplate={{
                  label: t("libraryTemplate.task"),
                  link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/add`,
                  linkLabel: t("libraryTemplate.task"),
                }}
              />
            </PopOver>
          </AddComponent>
          <div className="mt-2">
            <SecondaryHeading>{t("common.input.tags.label")}</SecondaryHeading>
            <div className="flex items-center gap-5 flex-wrap">
              {inputValues.tags.map((tag, index) => (
                <PreviewCard
                  color={tag.color}
                  title={tag.text}
                  icon={true}
                  className="m-0"
                  onCross={() => {
                    handleChangeInput(
                      "tags",
                      inputValues.tags.filter((_) => !isEqual(_.id, tag.id))
                    );
                  }}
                />
              ))}
            </div>
          </div>
          <AddComponent
            value={popoverStatus.tag}
            onClick={() => handlePopoverChange("tag", true)}
          >
            <PopOver
              open={popoverStatus.tag}
              onClose={() => handlePopoverChange("tag", false)}
              style={{
                top: "auto",
                left: "auto",
                right: "auto",
                bottom: "calc(100% + 15px)",
              }}
              toolTip={{ vertical: "bottom", horizontal: "center" }}
            >
              <DropDownCard
                title={t("common.input.tags.label")}
                listStyle
                filterSelectedItems
                className="relative-important"
                isEmpty={tags.length === 0}
                selectedValues={inputValues.tags.map((item) => item.id)}
                list={tags?.map((ele) => ({
                  ...ele,
                  index: ele.id,
                  text: ele.tag_title,
                }))}
                onSelect={(item) => {
                  if (inputValues.tags.find((i) => isEqual(i.id, item.id))) {
                    return;
                  }
                  setInputValues((pre) => ({
                    ...pre,
                    tags: [
                      ...pre.tags,
                      {
                        id: item.id,
                        text: item.tag_title,
                        color: item.color,
                      },
                    ],
                  }));
                }}
              />
            </PopOver>
          </AddComponent>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
